import { amountToDisplayValue, Color, ModuleLayout, Text } from '@billy/design-system'
import { ReactElement, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router'

import { BalanceSideProvider, useBalanceConfig, useBalanceLabels } from '@features/balanceLedger'
import { BalanceSide } from '@features/balanceLedger/enums/balanceSide'
import { getBalanceEmptyStateKey } from '@features/balanceLedger/utils/getBalanceEmptyStateKey'
import { ExpertReportHeaderActions, ExpertReportSubtitle, useExpertReportPeriod } from '@features/expertReports'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { SortDirection } from '../../enums/sortDirection'
import { PeriodValue } from '../../types/periodValue'
import { getOrganizationViewUrl } from '../../utils/routing/getOrganizationViewUrl'
import { EMPTY_VALUE_PLACEHOLDER } from './constants/emptyValuePlaceholder'
import {
  SuppliersBalanceFiltersProvider,
  useSuppliersBalanceFiltersContext,
} from './contexts/suppliersBalanceFiltersContext'
import { SuppliersBalanceEmptyState } from './elements/SuppliersBalanceEmptyState/SuppliersBalanceEmptyState'
import { SuppliersBalanceFiltersBar } from './elements/SuppliersBalanceFiltersBar'
import { SuppliersBalancePeriodSelect } from './elements/SuppliersBalancePeriodSelect'
import { SuppliersBalanceTable } from './elements/SuppliersBalanceTable'
import { SuppliersBalanceToolbar } from './elements/SuppliersBalanceToolbar'
import type { ItemsPerPage } from './elements/TableFooter'
import { SuppliersBalanceFilterType } from './enums/suppliersBalanceFilterType'
import { useFetchCreditorBalanceSummary } from './hooks/useFetchCreditorBalanceSummary'
import { useSuppliersBalanceSort } from './hooks/useSuppliersBalanceSort'
import { useSuppliersBalanceTableColumns } from './hooks/useSuppliersBalanceTableColumns'
import { useTrackSuppliersBalanceOverview } from './hooks/useTrackSuppliersBalanceOverview'
import { OverviewContent } from './styles'
import { SuppliersBalance } from './types/SuppliersBalance'
import { buildSuppliersBalanceNavQuery } from './utils/buildSuppliersBalanceNavQuery'

export const SuppliersBalanceOverviewContent = (): ReactElement => {
  const config = useBalanceConfig()
  const { bt } = useBalanceLabels()
  const { organization } = useUserOrganization()
  const { activeCount, filters, search } = useSuppliersBalanceFiltersContext()
  const { direction, setSort, sortBy, sortedColumnId } = useSuppliersBalanceSort()
  const navigate = useNavigate()
  const { encodedPeriod, periodType, setPeriod } = useExpertReportPeriod()

  const [page, setPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState<ItemsPerPage>(config.defaultPageSize)

  const sortDirection = direction === SortDirection.Desc ? 'DESC' : 'ASC'
  const contactIds = filters[SuppliersBalanceFilterType.Account]
  const balanceDirection = filters[SuppliersBalanceFilterType.BalanceDirection]
  const queryKey = [encodedPeriod, search, contactIds?.join(','), balanceDirection].join('|')

  const { data, pagination, totals, isLoading, isFetchingNewParams, isFetchingNewPeriod, isError } =
    useFetchCreditorBalanceSummary({
      organizationId: organization?.id ?? '',
      period: encodedPeriod,
      page,
      pageSize: itemsPerPage,
      sortBy,
      sortDirection,
      search,
      contactIds,
      balanceDirection,
    })

  const columns = useSuppliersBalanceTableColumns(organization?.baseCurrencyId, totals)

  const accountCount = pagination?.totalEntries ?? 0
  // isFetchingNewParams alone doesn't cover the initial fetch (no previous data for keepPreviousData to serve) or
  // an errored fetch with nothing cached, so tracking would otherwise settle on a false `resultsCount` of 0.
  const isResultsUnsettled = isLoading || isFetchingNewParams || isError
  const { trackPagination, trackSort } = useTrackSuppliersBalanceOverview({
    isResultsUnsettled,
    periodType,
    resultsCount: accountCount,
  })

  useEffect(() => {
    setPage(1)
  }, [encodedPeriod, sortBy, sortDirection, search, contactIds, balanceDirection])

  const handlePeriodChange = useCallback(
    (value?: PeriodValue): boolean => {
      if (!value) {
        return false
      }
      return setPeriod(value)
    },
    [setPeriod],
  )

  const handleRowClick = useCallback(
    (row: SuppliersBalance) => {
      if (organization) {
        // Carry the selected period to the detail view so it stays in sync across navigation (AUDIT-126).
        navigate(
          getOrganizationViewUrl(
            organization.url,
            config.getDetailRoute(row.id),
            buildSuppliersBalanceNavQuery({ period: encodedPeriod }),
          ),
        )
      }
    },
    [config, organization, navigate, encodedPeriod],
  )

  const handleSort = useCallback(
    (columnId: string, isDesc: boolean) => {
      setSort(columnId, isDesc)
      trackSort(columnId, isDesc)
    },
    [setSort, trackSort],
  )

  const handlePageChange = useCallback(
    (nextPage: number) => {
      setPage(nextPage)
      trackPagination({ itemsPerPage, pageNumber: nextPage })
    },
    [itemsPerPage, trackPagination],
  )

  const handleItemsPerPageChange = useCallback(
    (nextItemsPerPage: ItemsPerPage) => {
      setItemsPerPage(nextItemsPerPage)
      setPage(1)
      trackPagination({ itemsPerPage: nextItemsPerPage, pageNumber: 1 })
    },
    [trackPagination],
  )

  const headerBottomActions = (
    <ExpertReportHeaderActions>
      <SuppliersBalanceToolbar accountCount={accountCount} periodType={periodType} />
      <SuppliersBalanceFiltersBar encodedPeriod={encodedPeriod} periodType={periodType} />
    </ExpertReportHeaderActions>
  )

  const sideActions = <SuppliersBalancePeriodSelect encodedPeriod={encodedPeriod} onChange={handlePeriodChange} />

  const subtitle = (
    <ExpertReportSubtitle>
      <Text as="span" variant="bodyMD" weight="semiBold" color={Color.Grey500}>
        {totals ? amountToDisplayValue(totals.balance, false, organization?.baseCurrencyId) : EMPTY_VALUE_PLACEHOLDER}
      </Text>
    </ExpertReportSubtitle>
  )
  const emptyState = isError ? (
    bt('error.load_failed')
  ) : (
    <SuppliersBalanceEmptyState text={bt(getBalanceEmptyStateKey({ activeFilterCount: activeCount, search }))} />
  )

  return (
    <ModuleLayout
      fullHeight
      fullWidth
      headerBottomActions={headerBottomActions}
      sideActions={sideActions}
      subtitle={subtitle}
      title={bt('title')}
    >
      <OverviewContent>
        <SuppliersBalanceTable
          columns={columns}
          currentPage={page}
          data={data}
          emptyRowInfo={emptyState}
          isLoading={isLoading || isFetchingNewPeriod}
          itemsPerPage={itemsPerPage}
          onItemsPerPageChange={handleItemsPerPageChange}
          onPageChange={handlePageChange}
          onRowClick={handleRowClick}
          onSort={handleSort}
          queryKey={queryKey}
          sortedColumnId={sortedColumnId}
          totalPageItems={accountCount}
        />
      </OverviewContent>
    </ModuleLayout>
  )
}

export const SuppliersBalanceOverview = (): ReactElement => (
  <BalanceSideProvider side={BalanceSide.Supplier}>
    <SuppliersBalanceFiltersProvider>
      <SuppliersBalanceOverviewContent />
    </SuppliersBalanceFiltersProvider>
  </BalanceSideProvider>
)
